'use client';
import { datadogRum } from '@datadog/browser-rum';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useScrollToView from '@repo/hooks/dom/useScrollToView';
import { startTransition, useEffect, useRef, useState } from 'react';
import { PromotionalButton } from '../../../../PromotionalButton/PromotionalButton';
import { SummaryTableSeeMoreAccountsButton } from '../SummaryTableSeeMoreAccountsButton';
import { type SummaryTableRowWithDrivers } from '../types';
import { Row } from './components/Row';
import { SkeletonRow } from './components/SkeletonRow';

type Props = {
  rows: SummaryTableRowWithDrivers[];
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  showSkeleton?: boolean;
  isAccordionActiveButtonActive?: boolean;
  shouldDisplayPromoButton?: boolean;
};

export const MobileFirstSummaryTable = ({
  rows,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  showSkeleton = false,
  isAccordionActiveButtonActive = false,
  shouldDisplayPromoButton = false,
}: Props) => {
  const [rowsAreTruncated, setRowsAreTruncated] = useState(true);
  const truncationEnabled =
    isAccordionActiveButtonActive || (allowTruncatingRows && maxRows && rows.length > maxRows);
  const visibleRows =
    truncationEnabled && rowsAreTruncated ? rows.slice(0, numTruncatedRows) : rows;

  const hasMoreRowsThanVisibleCap = numTruncatedRows != null && rows.length > numTruncatedRows;
  const showTruncateButton = truncationEnabled && (hasMoreRowsThanVisibleCap || !rowsAreTruncated);

  const firedSummaryTableShown = useRef(false);

  useEffect(() => {
    if (!showSkeleton && !firedSummaryTableShown.current) {
      datadogRum.addTiming('summary_table_shown');
      firedSummaryTableShown.current = true;
    }
  }, [showSkeleton]);

  const viewMoreLessButtonHandler = () => {
    startTransition(() => {
      setRowsAreTruncated(t => !t);
    });

    if (!rowsAreTruncated) {
      useScrollToView('mobile-first-summary-table-headers', 'smooth', 0);
    }
  };

  const seeMoreLabel = rowsAreTruncated ? 'See more accounts' : 'See less accounts';
  const SeeMoreIcon = rowsAreTruncated ? ExpandMoreIcon : ExpandLessIcon;

  return (
    <>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'neutral.lighter2',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        }}
      >
        <Table>
          <TableHead id="mobile-first-summary-table-headers">
            <TableRow>
              <TableCell
                sx={{
                  width: '50%',
                  bgcolor: 'neutral.lightest',
                  textAlign: 'center',
                  borderBottom: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'neutral.lighter2',
                  paddingY: 2,
                  '&:first-of-type': {
                    paddingLeft: 3,
                  },
                }}
              >
                <Typography variant="accent">Bank</Typography>
              </TableCell>
              <TableCell
                sx={{
                  width: '50%',
                  textAlign: 'center',
                  borderBottom: '1px solid',
                  borderColor: 'neutral.lighter2',
                  paddingY: 2,
                }}
              >
                <Typography variant="accent">Details</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) =>
              showSkeleton ? (
                <SkeletonRow
                  key={`skeleton-${index}`}
                  driversLength={row.marketplaceEntity.drivers.length}
                />
              ) : (
                <Row key={row.marketplaceEntity.id} row={row} index={index} />
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showTruncateButton &&
        (isAccordionActiveButtonActive ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
            <SummaryTableSeeMoreAccountsButton
              label={seeMoreLabel}
              icon={SeeMoreIcon}
              onClick={viewMoreLessButtonHandler}
              fullWidth
            />
            <PromotionalButton isEnabled={shouldDisplayPromoButton} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Button
              variant="tertiary"
              component="span"
              disableRipple
              disableFocusRipple
              sx={{ mt: 1 }}
              onClick={viewMoreLessButtonHandler}
            >
              <Typography
                variant="accent"
                sx={{
                  fontWeight: 500,
                }}
              >
                {rowsAreTruncated ? 'View more' : 'View fewer'}
              </Typography>
            </Button>
            <PromotionalButton isEnabled={shouldDisplayPromoButton} />
          </Box>
        ))}
    </>
  );
};
